// Hour of AI variants of shared definitions: color pickers limited to the
// brand palette, with Hour of AI defaults.
import {ComponentDefinition} from '@contentful/experiences-sdk-react';

import {BadgeContentfulComponentDefinition} from '@/components/contentful/badge';
import {
  TextCollectionContentfulComponentDefinition,
  textCollectionColumnsDefinition,
} from '@/components/contentful/collections/textCollection';
import {ButtonLegacyContentfulComponentDefinition} from '@/components/contentful/corporateSite/buttonLegacy';
import {CustomTextContentfulComponentDefinition} from '@/components/contentful/customText';
import {CodeOrgDividerContentfulComponentDefinition} from '@/components/contentful/divider/dividerContentfulDefinition';
import {HeadingContentfulComponentDefinition} from '@/components/contentful/heading';
import {IconContentfulComponentDefinition} from '@/components/contentful/icon';
import {BrandLinkContentfulComponentDefinition} from '@/components/contentful/link';
import {ParagraphContentfulComponentDefinition} from '@/components/contentful/paragraph';
import {SectionCorporateSiteContentfulComponentDefinition} from '@/components/contentful/section';
import {
  BACKGROUND_MOTION_SPEEDS,
  BACKGROUND_MOTIONS,
  SECTION_EFFECT_OPTIONS,
} from '@/components/contentful/section/backgroundEffects';
import {SimpleListContentfulComponentDefinition} from '@/components/contentful/simpleList';

import {hourOfAiColorOptions, hourOfAiTextColorOptions} from './colorOptions';

type Variables = NonNullable<ComponentDefinition['variables']>;
type Option = {value: string; displayName: string};

const withVariables = (
  definition: ComponentDefinition,
  overrides: Record<string, {defaultValue: string; in: Option[]}>,
): ComponentDefinition => ({
  ...definition,
  variables: {
    ...definition.variables,
    ...Object.fromEntries(
      Object.entries(overrides).map(([name, {defaultValue, in: options}]) => [
        name,
        {
          ...(definition.variables as Variables)[name],
          defaultValue,
          validations: {in: options},
        },
      ]),
    ),
  },
});

export const HourOfAiHeadingDefinition = withVariables(
  HeadingContentfulComponentDefinition,
  {
    color: {
      defaultValue: 'purpleDark',
      in: hourOfAiTextColorOptions('purpleDark', {largeText: true}),
    },
  },
);

export const HourOfAiParagraphDefinition = withVariables(
  ParagraphContentfulComponentDefinition,
  {color: {defaultValue: 'black', in: hourOfAiTextColorOptions('black')}},
);

export const HourOfAiCustomTextDefinition = withVariables(
  CustomTextContentfulComponentDefinition,
  {
    color: {
      defaultValue: 'default',
      in: [
        {value: 'default', displayName: 'Default (from type)'},
        ...hourOfAiTextColorOptions(undefined, {largeText: true}),
      ],
    },
  },
);

export const HourOfAiBrandLinkDefinition = withVariables(
  BrandLinkContentfulComponentDefinition,
  {
    color: {
      defaultValue: 'color',
      in: [
        {value: 'color', displayName: 'Dark Purple (default)'},
        {value: 'black', displayName: 'Black'},
        {value: 'white', displayName: 'White'},
      ],
    },
  },
);

export const HourOfAiDividerDefinition = withVariables(
  CodeOrgDividerContentfulComponentDefinition,
  {color: {defaultValue: 'blueLight', in: hourOfAiColorOptions('blueLight')}},
);

export const HourOfAiIconDefinition = withVariables(
  IconContentfulComponentDefinition,
  {
    color: {defaultValue: 'purpleDark', in: hourOfAiColorOptions('purpleDark')},
    backgroundColor: {
      defaultValue: 'blueLight',
      in: hourOfAiColorOptions('blueLight'),
    },
  },
);

export const HourOfAiBadgeDefinition = withVariables(
  BadgeContentfulComponentDefinition,
  {
    color: {
      defaultValue: 'purple',
      in: [
        {value: 'black', displayName: 'Black'},
        {value: 'purple', displayName: 'Dark Purple (default)'},
        {value: 'pink', displayName: 'Pink'},
        {value: 'blue', displayName: 'Blue'},
      ],
    },
  },
);

export const HourOfAiButtonDefinition = withVariables(
  ButtonLegacyContentfulComponentDefinition,
  {
    color: {
      defaultValue: 'purple',
      in: [
        {value: 'purple', displayName: 'Dark Purple (default)'},
        {value: 'black', displayName: 'Black'},
        {value: 'white', displayName: 'White'},
      ],
    },
  },
);

const hourOfAiSection = withVariables(
  SectionCorporateSiteContentfulComponentDefinition,
  {
    background: {
      defaultValue: 'white',
      in: [
        ...hourOfAiColorOptions('white').filter(o => o.value === 'white'),
        ...hourOfAiColorOptions().filter(
          o => !['white', 'black'].includes(o.value),
        ),
        ...SECTION_EFFECT_OPTIONS,
        {value: 'black', displayName: 'Black'},
        {value: 'transparent', displayName: 'Transparent'},
      ],
    },
  },
);

// Motion only applies to the multi-color backgrounds (Aurora, Dusk).
export const HourOfAiSectionDefinition: ComponentDefinition = {
  ...hourOfAiSection,
  variables: {
    ...hourOfAiSection.variables,
    backgroundMotion: {
      displayName: 'Background motion',
      type: 'Text',
      group: 'style',
      description:
        'Slow motion for the Aurora and Dusk backgrounds. Stops for visitors who reduce motion.',
      defaultValue: 'none',
      validations: {in: [...BACKGROUND_MOTIONS]},
    },
    backgroundMotionSpeed: {
      displayName: 'Motion speed',
      type: 'Text',
      group: 'style',
      description:
        'How fast the background moves. For On scroll, how far it moves.',
      defaultValue: 'normal',
      validations: {
        in: BACKGROUND_MOTION_SPEEDS.map(({value, displayName}) => ({
          value,
          displayName,
        })),
      },
    },
  },
};

export const HourOfAiSimpleListDefinition = withVariables(
  SimpleListContentfulComponentDefinition,
  {
    type: {defaultValue: 'purpleDark', in: hourOfAiColorOptions('purpleDark')},
    textColor: {defaultValue: 'black', in: hourOfAiTextColorOptions('black')},
  },
);

export const HourOfAiTextCollectionDefinition: ComponentDefinition = {
  ...TextCollectionContentfulComponentDefinition,
  variables: {
    ...TextCollectionContentfulComponentDefinition.variables,
    ...textCollectionColumnsDefinition,
  },
};
